import { create } from "zustand";
import { persist } from 'zustand/middleware'
import { StructureObject, StructureObjectField, StructureStore } from "../utils/enums/structure-object";
import { ObjectsEnum } from "../utils/enums/basket";

import { structureProject, structureProjectOther } from "../utils/data/structure-object/projects";
import projectsIcon from '../images/icons/dark/projects-icon.svg';
import tasksIcon from '../images/icons/dark/tasks-icon.svg';
import clientsIcon from '../images/icons/dark/clients-icon.svg';
import companiesIcon from '../images/icons/dark/company-icon.svg';
import sellingIcon from '../images/icons/dark/sellings-icon.svg';
import financeIcon from '../images/icons/dark/finance-icon.svg';
import { structureTask, structureTaskOther } from "../utils/data/structure-object/tasks";
import { structureClient, structureClientOther } from "../utils/data/structure-object/client";
import { structureCompany, structureCompanyOther } from "../utils/data/structure-object/companies";
import { syncTableColumnsWithStructure } from "./useStructureColumnsStore";
import { structureSelling, structureSellingOther } from "../utils/data/structure-object/selling";
import { structureFinance, structureFinanceOther } from "../utils/data/structure-object/finance";

export const useStructureStore = create<StructureStore>()(
    persist(
        (set) => ({
            structures: {
                projects: {
                    entity: ObjectsEnum.PROJECTS,
                    entityTitle: "Проєкти",
                    entityIcon: projectsIcon,
                    fields: {
                        general: structureProject, other: structureProjectOther
                    }
                },
                tasks: {
                    entity: ObjectsEnum.TASKS,
                    entityTitle: "Завдання",
                    entityIcon: tasksIcon,
                    fields: {
                        general: structureTask, other: structureTaskOther
                    }
                },
                clients: {
                    entity: ObjectsEnum.CLIENTS,
                    entityTitle: "Клієнти",
                    entityIcon: clientsIcon,
                    fields: {
                        general: structureClient, other: structureClientOther
                    }
                },
                companies: {
                    entity: ObjectsEnum.COMPANIES,
                    entityTitle: "Компанії",
                    entityIcon: companiesIcon,
                    fields: {
                        general: structureCompany, other: structureCompanyOther
                    }
                },
                selling: {
                    entity: ObjectsEnum.SELLING,
                    entityTitle: "Продажі",
                    entityIcon: sellingIcon,
                    fields: {
                        general: structureSelling, other: structureSellingOther
                    }
                },
                expense: {
                    entity: ObjectsEnum.EXPENSE,
                    entityTitle: "Витрати",
                    entityIcon: financeIcon,
                    fields: {
                        general: structureFinance, other: structureFinanceOther
                    }
                },
                income: {
                    entity: ObjectsEnum.INCOME,
                    entityTitle: "Надходження",
                    entityIcon: financeIcon,
                    fields: {
                        general: structureFinance, other: structureFinanceOther
                    }
                },
            },

            updateField: (entity, key, patch) =>
                set((state) => {
                    const structure = state.structures[entity];
                    const fields = structure.fields;

                    const section = (Object.keys(fields) as (keyof typeof fields)[])
                        .find((s) => fields[s].some((f) => f.key === key));

                    if (!section) return state;

                    return {
                        structures: {
                            ...state.structures,
                            [entity]: {
                                ...structure,
                                fields: {
                                    ...fields,
                                    [section]: fields[section].map((f) =>
                                        f.key === key ? { ...f, ...patch } : f
                                    ),
                                },
                            },
                        },
                    };
                }),

            updateFieldVisibility: (entity, key, visible) =>
                set((state) => {
                    const structure = state.structures[entity];
                    const fields = structure.fields;

                    const section = (Object.keys(fields) as (keyof typeof fields)[])
                        .find((s) => fields[s].some((f) => f.key === key));

                    if (!section) return state

                    return {
                        structures: {
                            ...state.structures,
                            [entity]: {
                                ...structure,
                                fields: {
                                    ...fields,
                                    [section]: fields[section].map((f) => {
                                        if (f.key === key) {
                                            return { ...f, visible };
                                        }
                                        return f;
                                    }),
                                },
                            },
                        },
                    };
                }),

            addField: (entity, newItem) => set((state) => {
                const structure = state.structures[entity];
                const fields = structure.fields;

                return {
                    structures: {
                        ...state.structures,
                        [entity]: {
                            ...structure,
                            fields: {
                                ...fields,
                                general: [...structure.fields.general, newItem],
                            },
                        },
                    },
                };
            }),

            deleteField: (entity, item) =>
                set((state) => {
                    const structure = state.structures[entity];
                    if (!structure || !item.isCreated) return state;

                    return {
                        structures: {
                            ...state.structures,
                            [entity]: {
                                ...structure,
                                fields: {
                                    ...structure.fields,
                                    general: structure.fields.general.filter(
                                        (f) => f.key !== item.key
                                    ),
                                },
                            },
                        },
                    };
                }),

        }),
        {
            name: 'clentyra-structure',
            version: 1
        }
    )
)

useStructureStore.subscribe((state, prevState) => {
    const structures = state.structures;

    Object.keys(structures).forEach((entity) => {
        syncTableColumnsWithStructure(
            entity,
            structures[entity].fields
        );

    });
});



type FieldSection = keyof StructureObject["fields"];

export function findStructureFieldByKey(
    fields: StructureObject["fields"],
    key: string
): StructureObjectField | undefined {
    for (const section of Object.keys(fields) as FieldSection[]) {
        const field = fields[section].find((f) => f.key === key);
        if (field) return field;
    }
    return undefined;
}

export const shouldRenderField = (field?: StructureObjectField) =>
    !!(field?.isRequired || field?.visible);